/**
 * 文字衛星 —— 在星空中以衛星軌道環繞的星球名稱字元
 * 僅在從心遊語宙跳轉而來時顯示（fromStar 存在）
 *
 * 軌道設計：低傾角大半徑，讓字元從地平線升起、劃過頭頂，
 * 不需仰頭很久就能看見。
 */
import { useRef, useMemo, useEffect } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import * as THREE from 'three/webgpu'
import { texture, uv, vec4, float, smoothstep, length } from 'three/tsl'
import { useGameStore, CameraMode } from '../../core/store/gameStore'

/** 在 canvas 上渲染帶光暈的中文字元（512px 高解析度） */
function createCharTexture(char: string, glowColor: string, size = 512): THREE.CanvasTexture {
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')!
  ctx.clearRect(0, 0, size, size)

  const fontFamily = '"Noto Serif TC", "Source Han Serif TC", "STSong", serif'
  const fontSize = size * 0.42

  ctx.font = `bold ${fontSize}px ${fontFamily}`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  const cx = size / 2
  const cy = size / 2

  // 最外層：大面積微光暈散（域色氛圍）
  ctx.shadowColor = glowColor
  ctx.shadowBlur = size * 0.25
  ctx.fillStyle = glowColor
  ctx.globalAlpha = 0.25
  for (let i = 0; i < 4; i++) ctx.fillText(char, cx, cy)

  // 中層：域色光暈
  ctx.shadowBlur = size * 0.12
  ctx.fillStyle = glowColor
  ctx.globalAlpha = 0.5
  ctx.fillText(char, cx, cy)

  // 內層：白色字體帶域色投影
  ctx.shadowColor = glowColor
  ctx.shadowBlur = size * 0.06
  ctx.fillStyle = '#ffffff'
  ctx.globalAlpha = 0.95
  ctx.fillText(char, cx, cy)

  // 核心：純白清晰筆劃
  ctx.shadowBlur = 0
  ctx.globalAlpha = 1
  ctx.fillStyle = '#ffffff'
  ctx.fillText(char, cx, cy)

  const tex = new THREE.CanvasTexture(canvas)
  tex.colorSpace = THREE.SRGBColorSpace
  return tex
}

/** 單個字元衛星 */
function CharSatellite({
  char, color, orbitRadius, orbitSpeed, angleOffset, tiltAngle, tiltAxis, baseHeight, scale,
}: {
  char: string
  color: string
  orbitRadius: number
  orbitSpeed: number
  angleOffset: number
  tiltAngle: number
  tiltAxis: number
  baseHeight: number
  scale: number
}) {
  const spriteRef = useRef<THREE.Sprite>(null)
  const { camera } = useThree()

  const { tex, material } = useMemo(() => {
    const tex = createCharTexture(char, color)
    const mat = new THREE.SpriteNodeMaterial({
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    })
    const uvNode = uv()
    const sampled = texture(tex, uvNode)
    const d = length(uvNode.sub(0.5))
    const edgeFade = smoothstep(float(0.5), float(0.35), d)
    mat.colorNode = vec4(sampled.rgb, sampled.a.mul(edgeFade))
    return { tex, material: mat }
  }, [char, color])

  useFrame(({ clock }) => {
    if (!spriteRef.current) return
    const t = clock.elapsedTime * orbitSpeed + angleOffset

    // 圓形軌道 → 繞 Y 軸方向旋轉
    let x = Math.cos(t) * orbitRadius
    let z = Math.sin(t) * orbitRadius
    let y = baseHeight

    // 軌道傾斜：繞水平軸旋轉，讓字元的軌跡從地平線劃過頭頂
    const sinTilt = Math.sin(tiltAngle)
    const cosTilt = Math.cos(tiltAngle)
    // 繞 tiltAxis 方向（XZ 平面上的方向）傾斜
    const cosA = Math.cos(tiltAxis)
    const sinA = Math.sin(tiltAxis)
    // 先轉到 tiltAxis 局部座標
    const localX = x * cosA + z * sinA
    const localZ = -x * sinA + z * cosA
    // 繞 localX 軸傾斜
    const tiltedY = y * cosTilt - localZ * sinTilt
    const tiltedZ = y * sinTilt + localZ * cosTilt
    // 轉回世界座標
    const worldX = localX * cosA - tiltedZ * sinA
    const worldZ = localX * sinA + tiltedZ * cosA

    spriteRef.current.position.set(
      camera.position.x + worldX,
      camera.position.y + tiltedY,
      camera.position.z + worldZ,
    )
    spriteRef.current.scale.setScalar(scale)
  })

  useEffect(() => () => { tex.dispose(); material.dispose() }, [tex, material])

  const sprite = useMemo(() => {
    const s = new THREE.Sprite(material)
    s.frustumCulled = false
    return s
  }, [material])

  return <primitive ref={spriteRef} object={sprite} />
}

export function TextSatellites() {
  const fromStar = useGameStore(s => s.fromStar)
  const starColor = useGameStore(s => s.starColor)
  const cameraMode = useGameStore(s => s.cameraMode)

  if (!fromStar) return null

  const chars = fromStar.split('')
  const color = starColor || '#aaccff'
  const count = chars.length

  // FPV 模式下字體更大（地面仰望視角）
  const baseScale = cameraMode === CameraMode.FPV ? 12 : 8

  return (
    <group>
      {chars.map((char, i) => {
        // 每個字元在不同軌道上，間距足夠但不會太散
        const angle = (i / count) * Math.PI * 2
        return (
          <CharSatellite
            key={`${fromStar}-${i}`}
            char={char}
            color={color}
            orbitRadius={100 + i * 8}
            orbitSpeed={0.04 + (i % 3) * 0.008}
            angleOffset={angle}
            tiltAngle={0.5 + i * 0.12}
            tiltAxis={i * 0.7}
            baseHeight={8 + i * 3}
            scale={baseScale + i * 0.8}
          />
        )
      })}
    </group>
  )
}
